import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { DownloadTheApp, TrustpilotReviews } from 'wanderu-app-gettheapp';
import {
    CarrierSection,
    InfoSection,
    TableOfContentsSection,
} from 'wanderu-app-seo';
import { Footer } from 'wanderu-component-footer';
import Helmet from 'wanderu-component-helmet';
import BaseHeroHeader from 'wanderu-component-hero-header';
import { NotFound } from 'wanderu-component-not-found';
import {
    BusLoader,
    FAQSection,
    Section,
    ValueProps,
    ValueStatement,
} from 'wanderu-components';
import {
    i18nCoordinator,
    localize,
    selectLocale,
} from 'wanderu-i18n';
import { Widget } from 'wanderu-widget';

import AmenitiesSection from '../../components/AmenitiesSection/AmenitiesSection';
import InTheArea from '../../components/InTheArea/InTheArea';
import MapSection from '../../components/MapSection';
import RoutesTable from '../../components/RoutesTable/RoutesTable';
import TerminalOverview from '../../components/TerminalOverview/TerminalOverview';
import { NAV_ITEMS } from '../../constants';
import {
    fetchStationGuideAction,
    stationGuideSelectors,
} from '../../ducks';
import stationGuideConfig from '../../locales.config';
import {
    isInvalidUrl,
    localizeStructuredData,
} from '../../utils';

const { namespace, supportedLocales } = stationGuideConfig;

const propTypes = {
    amenitiesContent: PropTypes.arrayOf(
        PropTypes.shape({
            description: PropTypes.string,
            title: PropTypes.string,
        })
    ),
    carriers: PropTypes.arrayOf(PropTypes.object),
    content: PropTypes.shape(),
    faqContent: PropTypes.arrayOf(
        PropTypes.shape({
            prefix: PropTypes.string,
            text: PropTypes.string,
            title: PropTypes.string,
        })
    ),
    fetchContent: PropTypes.func.isRequired,
    isFetching: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    location: PropTypes.shape(),
    match: PropTypes.shape().isRequired,
    metaImage: PropTypes.shape({
        alt: PropTypes.string,
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
        src: PropTypes.string.isRequired,
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
    }),
    nearbyContent: PropTypes.arrayOf(
        PropTypes.shape({
            stat: PropTypes.shape({
                mi: PropTypes.number,
            }),
            text: PropTypes.string,
        })
    ),
    notFound: PropTypes.bool,
    routeItems: PropTypes.arrayOf(
        PropTypes.shape({
            avgPrice: PropTypes.number,
            avgTime: PropTypes.number,
            dailyTrips: PropTypes.number,
            destination: PropTypes.string,
            url: PropTypes.string,
        })
    ),
    staticContext: PropTypes.shape({
        status: PropTypes.number,
    }),
    stationImages: PropTypes.arrayOf(
        PropTypes.shape({
            metadata: PropTypes.shape({
                alt: PropTypes.string,
            }),
            url: PropTypes.string,
        })
    ),
    stationMaps: PropTypes.arrayOf(
        PropTypes.shape({
            metadata: PropTypes.shape({
                alt: PropTypes.string,
            }),
            url: PropTypes.string,
        })
    ),
    structuredData: PropTypes.arrayOf(
        PropTypes.shape({
            description: PropTypes.string,
            mainEntity: PropTypes.arrayOf(
                PropTypes.shape({
                    acceptedAnswer: PropTypes.shape({
                        text: PropTypes.string,
                    }),
                    name: PropTypes.string,
                })
            ),
        })
    ),
    t: PropTypes.func,
    wid: PropTypes.string,
};

const defaultProps = {
    content: {},
    location: {},
    wid: '',
};

function StationGuide(props) {
    const {
        amenitiesContent,
        carriers,
        content,
        faqContent,
        fetchContent,
        isFetching,
        locale,
        location,
        metaImage,
        nearbyContent,
        notFound,
        routeItems,
        staticContext,
        stationImages,
        stationMaps,
        structuredData,
        t,
        wid,
    } = props;

    useEffect(() => {
        i18nCoordinator.registerTranslatableApp({
            ...stationGuideConfig,
            translationFetchFn: () => { },
        });

        return () => {
            i18nCoordinator.unregisterTranslatableApp(namespace);
        };
    }, []);

    useEffect(() => {
        if (Object.keys(content).length) {
            return;
        }

        fetchContent(wid, locale);
    }, [content, fetchContent, locale, wid]);

    useEffect(() => {
        if (!Object.keys(content).length) {
            return;
        }

        i18nCoordinator.updateTranslations(locale, content, namespace);
    }, [content, locale]);

    if (notFound) {
        if (staticContext) {
            staticContext.status = 404;
        }
        return <NotFound status={staticContext?.status} />;
    }

    if (isFetching || !Object.keys(content).length) {
        return (
            <BusLoader large />
        );
    }

    const localizedStructuredData = localizeStructuredData({
        locale,
        structuredData,
        t,
    });

    const filterNavItems = navItems => {
        if (!t('mapDescription')) {
            return navItems.filter(item => item.anchor !== '#map-header-section');
        }

        return navItems;
    };

    return (
        <>
            <Helmet
                canonicalPath={t('canonicalSlug')}
                description={t('metaPageDescription')}
                image={metaImage}
                structuredData={localizedStructuredData}
                supportedLocales={supportedLocales}
                title={t('metaPageTitle')}
            />
            <BaseHeroHeader
                title={t('widgetTitle')}
                widgetProps={{
                    defaultOriginInput: location.formattedAddress,
                    defaultOriginPlaceId: location.placeId,
                }}
            />
            <Section>
                <ValueProps />
            </Section>
            <TrustpilotReviews locale={locale} />
            <DownloadTheApp campaign="web-station_guide" />
            <TableOfContentsSection
                namespace={namespace}
                navItems={filterNavItems(NAV_ITEMS)}
            />
            <TerminalOverview
                description={t('introDescription')}
                id="overview-header-section"
                location={location}
                namespace={namespace}
                stationImages={stationImages}
                title={t('introHeader', {
                    stationName: t('stationName'),
                })}
            />
            <FAQSection title={t('faqHeader')}>
                {faqContent.map((info) => (
                    <InfoSection
                        id={info.prefix}
                        infoText={info.text}
                        infoTitle={t(info.title)}
                        isOpen={false}
                        key={info.prefix}
                    />
                ))}
            </FAQSection>
            <AmenitiesSection
                amenitiesContent={amenitiesContent}
                namespace={namespace}
                sectionTitle={t('amenitiesHeader')}
            />
            {t('mapDescription')
                && <Section aria-labelledby="map-header" id="map-header-section">
                    <MapSection
                        mapDescription={t('mapDescription')}
                        mapHeader={t('mapHeader')}
                        stationMaps={stationMaps}
                    />
                </Section>
            }
            <Section aria-labelledby="nearby-header" id="nearby-header-section">
                <InTheArea
                    places={nearbyContent}
                    title={t('nearbyHeader')}
                />
            </Section>
            <CarrierSection
                data={carriers}
                description={t('carriersDescription')}
                namespace={namespace}
                title={t('carriersHeader')}
                hideCarrierStatsTable
            />
            <Section id="routes-header-section">
                <RoutesTable
                    namespace={namespace}
                    routeItems={routeItems}
                />
            </Section>
            <ValueStatement namespace={'baseApp'} />
            <Footer limitLinkFlow />
        </>
    );
}

StationGuide.serverFetchData = async (opts) => {
    // return; // proxy settings in prod prevents this from working right now
    const {
        dispatch,
        params,
        getState,
        i18nCoordinator: serverI18nCoordinator,
    } = opts;

    const { station, locale } = params;

    if (isInvalidUrl(station)) {
        return;
    }

    const wid = station.split('--')[1].toUpperCase();

    await dispatch(fetchStationGuideAction.fetchPageContent(wid, locale));

    const stationId = `en-us-${wid.replace('/', '')}`;

    await Promise.all([
        serverI18nCoordinator.registerTranslatableApp({
            ...stationGuideConfig,
            translationFetchFn: () => {
                const apiTranslations = stationGuideSelectors.selectContent(getState(), stationId);

                return {
                    ...apiTranslations,
                };
            },
        }),
        Widget.serverFetchData(opts),
    ]);
};

const mapStateToProps = (state, ownProps) => {
    const { url } = ownProps.match;

    if (isInvalidUrl(url)) {
        return {
            notFound: true,
        };
    }

    const wid = url.split('--')[1].replace('/', '').toUpperCase();
    const locale = selectLocale(state);
    const stationId = `${locale}-${wid}`;
    const content = stationGuideSelectors.selectContent(state, stationId);
    const faqContent = content
        ? stationGuideSelectors.selectFaqContent(state, stationId)
        : [];
    const noContent = !content || !Object.keys(content).length;
    const fetching = !fetchStationGuideAction.selectError(state, stationId)
        && !fetchStationGuideAction.selectSuccess(state, stationId);
    const notFound = noContent && !fetching;
    const stationImages = stationGuideSelectors.selectStationImages(state, stationId) || [];
    const metaImage = stationGuideSelectors.selectMetaImage(state, stationId);
    const location = stationGuideSelectors.selectLocation(state, stationId);

    return {
        amenitiesContent: stationGuideSelectors.selectAmenities(state, stationId),
        carriers: stationGuideSelectors.selectCarriers(state, stationId),
        content,
        faqContent,
        isFetching: fetchStationGuideAction.selectWorking(state, stationId),
        locale,
        location,
        metaImage,
        nearbyContent: stationGuideSelectors.selectNearbyContent(state, stationId),
        notFound,
        routeItems: stationGuideSelectors.selectRoutes(state, stationId),
        stationId,
        stationImages,
        stationMaps: stationGuideSelectors.selectStationMaps(state, stationId),
        structuredData: stationGuideSelectors.selectStructuredData(
            state,
            stationId,
        ),
        wid,
    };
};

const mapDispatchToProps = {
    fetchContent: fetchStationGuideAction.fetchPageContent,
};

StationGuide.propTypes = propTypes;
StationGuide.defaultProps = defaultProps;

const localizedStationGuide = localize(StationGuide, ['t'], stationGuideConfig.namespace);
// eslint-disable-next-line react-redux/prefer-separate-component-file
export default connect(mapStateToProps, mapDispatchToProps)(localizedStationGuide);
